import { HTTP_STATUS_CODE, IHttpGet } from '../../adapters/http';
import { ISetAssetColorRepo } from '../../interfaces/asset-color';
import {
  IComparisonAssetHeader,
  IGetAssetMemory,
  ISetAssetMemory,
} from '../../interfaces';
import { InvalidAssetError } from '../../helpers';
import { IRequestDTO, IResponseDTO } from './dto';

export class ComparisonAssetHeaderUseCase {
  constructor(
    private readonly url: string,
    private readonly http: IHttpGet<IComparisonAssetHeader>,
    private readonly setAssetColorRepo: ISetAssetColorRepo,
    private readonly assetMemoryRepo: IGetAssetMemory<IComparisonAssetHeader> &
      ISetAssetMemory<IComparisonAssetHeader>
  ) {}

  async execute({ assetsIds }: IRequestDTO): Promise<IResponseDTO> {
    const assetsIdsWithNoRepeatValues = [...new Set(assetsIds)];
    let error = null;

    const assets: IComparisonAssetHeader[] = await Promise.all(
      assetsIdsWithNoRepeatValues.map(async (id) => {
        if (id) {
          const inMemory = this.assetMemoryRepo.get(id);

          if (inMemory) {
            return inMemory;
          }

          const { statusCode, body } = await this.http.get({
            url: `${this.url}/${id}`,
          });

          if (statusCode === HTTP_STATUS_CODE.ok) {
            const newAsset = {
              identifier: id,
              nicename: body.nicename,
              slug: `/${body.slug}`,
              first_quote_date: body.first_quote_date,
              last_quote_date: body.last_quote_date,
              last_update: body.last_update,
              has_quotes: body.has_quotes,
              id: id.split(':')[0],
              assetType: id.split(':')[1],
              class: null,
              subclass: null,
            };

            this.assetMemoryRepo.set(id, newAsset);

            return newAsset;
          }
        }

        return null;
      })
    );

    const assetsHeaderWithoutNullValues = assets.filter(Boolean);

    const assetsHeaderListValidated = assetsHeaderWithoutNullValues.reduce(
      (validAssets, assetHeader) => {
        try {
          this.isAssetComparable(
            assetHeader,
            Object.values(assetsHeaderWithoutNullValues)
          );

          return [...validAssets, assetHeader];
        } catch (err) {
          if (err instanceof InvalidAssetError) {
            error = err.message;
          }
        }

        return validAssets;
      },
      [] as IComparisonAssetHeader[]
    );

    const assetsHeaderWithColors = assetsHeaderListValidated.map((asset) => ({
      ...asset,
      color: this.setAssetColorRepo.setColor(asset.identifier),
    }));

    return {
      assetsHeaderList: assetsHeaderWithColors,
      error,
    };
  }

  private hasIntersectionInPeriods = (
    asset: IComparisonAssetHeader,
    toCompare: IComparisonAssetHeader
  ) => {
    return (
      asset.first_quote_date <= toCompare.last_quote_date &&
      asset.last_quote_date >= toCompare.first_quote_date
    );
  };

  private async isAssetComparable(
    currentAsset: IComparisonAssetHeader,
    allAssets: IComparisonAssetHeader[]
  ) {
    if (!currentAsset.has_quotes) {
      throw new InvalidAssetError(
        `O ativo ${currentAsset.nicename} se encontra em estado pré-operacional e não pode ser adicionado à comparação.`,
        currentAsset
      );
    }

    const existInComparison = allAssets.find(
      ({ identifier }) => identifier === currentAsset.identifier
    );

    if (existInComparison) {
      return;
    }

    const isComparable = allAssets.every((toCompare) =>
      this.hasIntersectionInPeriods(currentAsset, toCompare)
    );

    if (!isComparable) {
      throw new InvalidAssetError(
        `Infelizmente não é possível comparar o ativo ${currentAsset.nicename} com os outros existentes na mesma comparação pois eles não coexistem no mesmo período.`,
        currentAsset
      );
    }
  }
}
